import { useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Check, X } from "lucide-react";
import {
  AnswerButton,
  TRUE_FALSE_OPTION_LABELS,
  TRUE_FALSE_OPTIONS_GRID,
} from "@/components/storyboard/quiz/TrueFalseAnswerButton";
import { QuizChallengeHeading } from "@/components/storyboard/quiz/QuizChallengeHeading";
import QuizCheckNextAction from "@/components/storyboard/quiz/QuizCheckNextAction";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import { CodeBlock } from "@/components/storyboard/code/CodeBlock";
import { GradedFeedback } from "@/components/storyboard/GradedFeedback";
import {
  buildCodeTrueFalseEntryReview,
  resolveCodeTrueFalseEntry,
} from "@/components/storyboard/quiz/review/buildEntryReview";
import type { QuizCodeTrueFalseEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";

export interface QuizCodeTrueFalseState {
  currentIndex: number;
  selectedAnswer: boolean | null;
  isSubmitted: boolean;
  isCorrect: boolean | null;
  correctCount: number;
}

export interface QuizCodeTrueFalseProps extends HiddenVerdictProps {
  entries: QuizCodeTrueFalseEntryData[];
  /** Server verdict for the selected answer of one entry. */
  onValidate: (answer: boolean, entryIndex: number) => Promise<boolean>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizCodeTrueFalseState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizCodeTrueFalseState, index: number) => void;
}

const QuizCodeTrueFalse = ({
  entries,
  onValidate,
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizCodeTrueFalseProps) => {
  const [state, setState] = useState<QuizCodeTrueFalseState>(() => {
    if (initialPartial) return initialPartial;
    return {
      currentIndex: initialIndex ?? 0,
      selectedAnswer: null,
      isSubmitted: false,
      isCorrect: null,
      correctCount: 0,
    };
  });

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { currentIndex, selectedAnswer, isSubmitted, isCorrect } = state;

  if (!entries || entries.length === 0) return null;

  const currentEntry = entries[currentIndex];
  if (!currentEntry) return null;

  const { codeSnippet, statementText } =
    resolveCodeTrueFalseEntry(currentEntry);

  const handleAnswerSelect = (answer: boolean) => {
    if (!isSubmitted) {
      hasInteractedRef.current = true;
      setState((prev) => ({ ...prev, selectedAnswer: answer }));
    }
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null) return;

    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => onValidate(selectedAnswer, currentIndex),
        onAdvance
      );
      return;
    }

    const correct = await onValidate(selectedAnswer, currentIndex);
    const correctCount = state.correctCount + (correct ? 1 : 0);

    reviewRef.current[currentIndex] = buildCodeTrueFalseEntryReview(
      currentEntry,
      selectedAnswer,
      correct
    );

    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      isSubmitted: true,
      isCorrect: correct,
      correctCount,
    }));

    if (currentIndex === entries.length - 1) {
      onComplete?.(
        { correct: correctCount, total: entries.length },
        { items: reviewRef.current }
      );
      if (correct) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        selectedAnswer: null,
        isSubmitted: false,
        isCorrect: null,
      }));
    }
  };

  const renderCodeSnippet = () => {
    if (!codeSnippet) return null;
    return (
      <div className="pl-3">
        <CodeBlock code={codeSnippet} />
      </div>
    );
  };

  const renderVerdict = () => {
    if (!isSubmitted || isCorrect === null) return null;

    const feedback = isCorrect
      ? (currentEntry.userFeedback?.positive ?? "")
      : (currentEntry.userFeedback?.negative ?? "");

    return (
      <GradedFeedback
        result={{ correct: isCorrect, feedback }}
        className="w-full"
      />
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 w-full max-w-2xl flex-1 flex-col">
        <div className="shrink-0 space-y-4">
          <QuizEntryCounter currentIndex={currentIndex} total={entries.length}>
            <QuizChallengeHeading challenge={statementText} />
          </QuizEntryCounter>
          {renderCodeSnippet()}
        </div>

        <div className="flex flex-1 flex-col items-center justify-start gap-4 pt-8">
          <TaskInstructionHint className="self-start">
            {currentEntry.instruction}
          </TaskInstructionHint>
          <div className={TRUE_FALSE_OPTIONS_GRID}>
            <AnswerButton
              value={true}
              label={TRUE_FALSE_OPTION_LABELS.true}
              icon={Check}
              isSelected={selectedAnswer === true}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onSelect={handleAnswerSelect}
            />
            <AnswerButton
              value={false}
              label={TRUE_FALSE_OPTION_LABELS.false}
              icon={X}
              isSelected={selectedAnswer === false}
              isSubmitted={isSubmitted}
              isCorrect={isCorrect}
              onSelect={handleAnswerSelect}
            />
          </div>
          {renderVerdict()}
        </div>

        <div className="flex shrink-0 justify-center pt-8">
          <QuizCheckNextAction
            isSubmitted={isSubmitted}
            hasNextQuestion={currentIndex < entries.length - 1}
            canSubmit={selectedAnswer !== null}
            onSubmit={handleSubmit}
            onNext={handleNext}
            submitLabel={
              collectsWithoutVerdict(revealFeedback) ? advanceLabel : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

export default QuizCodeTrueFalse;
